import React from 'react';
import { Pencil, Trash2, Folder, ChevronRight, Pin } from 'lucide-react';

/** Constant Colors - used across hierarchy for consistency */
const TAG_COLORS = {
    indigo: { bg: 'bg-indigo-500/15', text: 'text-indigo-400', border: 'border-indigo-500/20' },
    emerald: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/20' },
    amber: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/20' },
    rose: { bg: 'bg-rose-500/15', text: 'text-rose-400', border: 'border-rose-500/20' },
    purple: { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/20' },
    sky: { bg: 'bg-sky-500/15', text: 'text-sky-400', border: 'border-sky-500/20' },
    teal: { bg: 'bg-teal-500/15', text: 'text-teal-400', border: 'border-teal-500/20' },
    slate: { bg: 'bg-slate-500/15', text: 'text-slate-400', border: 'border-slate-500/20' },
};

const TAG_COLORS_LIGHT = {
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
    sky: { bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
};

/**
 * Note Entry Card — Now follows the 'Folder' style of hierarchical storyboards
 */
const NoteEntryCard = ({ storyboard, isDarkMode, onClick, onEdit, onDelete, onTogglePin }) => {
    const { name, tags = [], is_pinned } = storyboard;

    return (
        <div
            onClick={() => onClick?.(storyboard)}
            className={`
                group relative p-4 rounded-xl border flex items-center justify-between
                transition-all duration-300 hover:-translate-y-1 cursor-pointer
                ${isDarkMode
                    ? 'bg-slate-800/80 border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 shadow-sm shadow-slate-900/20'
                    : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm shadow-slate-200/50'
                }
                ${is_pinned ? (isDarkMode ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-indigo-200 bg-indigo-50/30') : ''}
            `}
        >
            <div className="flex items-center gap-3 min-w-0">
                {/* Folder Icon Box */}
                <div className={`
                    p-2.5 rounded-lg shrink-0 transition-colors
                    ${is_pinned
                        ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                        : (isDarkMode ? 'bg-slate-700 group-hover:bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 group-hover:bg-indigo-100 text-indigo-600')
                    }
                `}>
                    <Folder size={18} />
                </div>

                {/* Info Container */}
                <div className="min-w-0">
                    <h3 className={`text-xs font-bold truncate leading-tight ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                        {name}
                    </h3>

                    {/* Tags List */}
                    <div className="flex flex-wrap gap-1 mt-0.5">
                        {tags.length > 0 ? (
                            tags.map(t => {
                                const colors = isDarkMode ? (TAG_COLORS[t.color] || TAG_COLORS.slate) : (TAG_COLORS_LIGHT[t.color] || TAG_COLORS_LIGHT.slate);
                                return (
                                    <span
                                        key={t.id}
                                        className={`text-[7px] px-1 py-0.5 rounded font-bold uppercase tracking-tighter border ${colors.bg} ${colors.text} ${colors.border}`}
                                    >
                                        {t.name}
                                    </span>
                                );
                            })
                        ) : (
                            <span className="text-[8px] font-medium opacity-20 uppercase tracking-widest">No tags</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Actions & Chevron */}
            <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                {!!onTogglePin && (
                    <button
                        onClick={() => onTogglePin(storyboard.id)}
                        className={`p-1.5 rounded-lg transition-colors ${!!is_pinned ? 'text-indigo-500 bg-indigo-500/10' : (isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100')}`}
                    >
                        <Pin size={12} fill={!!is_pinned ? "currentColor" : "none"} />
                    </button>
                )}
                {onEdit && (
                    <button
                        onClick={() => onEdit(storyboard)}
                        className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-indigo-500/10 text-slate-400 hover:text-indigo-400' : 'hover:bg-indigo-50 text-slate-400 hover:text-indigo-600'}`}
                    >
                        <Pencil size={12} />
                    </button>
                )}
                {onDelete && (
                    <button
                        onClick={() => onDelete(storyboard)}
                        className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-rose-500/10 text-slate-400 hover:text-rose-400' : 'hover:bg-rose-50 text-slate-400 hover:text-rose-600'}`}
                    >
                        <Trash2 size={12} />
                    </button>
                )}
                <ChevronRight size={14} className={`ml-1 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
            </div>

            {/* Subtle Pin indicator for un-hovered state */}
            {!!is_pinned && (
                <div className="absolute top-2 right-2 pointer-events-none opacity-40 group-hover:opacity-0 transition-opacity">
                    <Pin size={10} fill="currentColor" className="text-indigo-500 rotate-45" />
                </div>
            )}
        </div>
    );
};

export { TAG_COLORS, TAG_COLORS_LIGHT };
export default NoteEntryCard;
